import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, UserCheck, UserRound } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { buscarClientes, clientePorCpf } from "./api";

const soDigitos = (v) => String(v || "").replace(/\D/g, "");
const mascaraCpf = (v) => soDigitos(v).slice(0, 11)
  .replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");

/**
 * Garçom põe uma pessoa na mesa (quem não tem celular ou dividiu o celular).
 * Começa pelo CPF, com busca no cadastro (dono, 25/09/2026):
 *  - CPF com cadastro → mostra o cliente e adiciona ele (conta, cashback);
 *  - CPF sem cadastro → pede o nome e cria o cliente com esse CPF;
 *  - "Sem CPF, só o nome" → só um nome nesta mesa, sem cadastro (visual).
 * Enquanto digita (5+ dígitos) sugere clientes cujo CPF começa assim.
 */
export default function NovaPessoaDialog({ aberto, onFechar, onCriar }) {
  const [cpf, setCpf] = useState("");
  const [nome, setNome] = useState("");
  const [soNome, setSoNome] = useState(false);
  const [achado, setAchado] = useState(null);      // cliente do CPF completo
  const [estado, setEstado] = useState("vazio");    // vazio | buscando | achado | novo | invalido
  const [sugestoes, setSugestoes] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const ultima = useRef(0);

  useEffect(() => {
    if (aberto) { setCpf(""); setNome(""); setSoNome(false); setAchado(null); setEstado("vazio"); setSugestoes([]); }
  }, [aberto]);

  const dig = soDigitos(cpf);
  useEffect(() => {
    if (soNome) return undefined;
    const minha = ++ultima.current;
    setAchado(null);
    if (dig.length === 11) {
      setEstado("buscando"); setSugestoes([]);
      clientePorCpf(dig)
        .then(({ data }) => { if (minha === ultima.current) { setAchado(data); setEstado("achado"); } })
        .catch((e) => { if (minha === ultima.current) setEstado(e?.response?.status === 400 ? "invalido" : "novo"); });
      return undefined;
    }
    setEstado("vazio");
    if (dig.length < 5) { setSugestoes([]); return undefined; }
    const t = setTimeout(() => {
      buscarClientes(dig)
        .then(({ data }) => {
          if (minha !== ultima.current) return;
          const lista = (Array.isArray(data) ? data : []).filter((c) => soDigitos(c.cpf).startsWith(dig)).slice(0, 4);
          setSugestoes(lista);
        })
        .catch(() => { if (minha === ultima.current) setSugestoes([]); });
    }, 300);
    return () => clearTimeout(t);
  }, [dig, soNome]);

  const nomeFinal = estado === "achado" ? (achado?.nome || "") : nome.trim();
  const pode = !salvando && (soNome
    ? nome.trim().length >= 2
    : (estado === "achado" || (estado === "novo" && nome.trim().length >= 2)));

  const salvar = async (e) => {
    e.preventDefault();
    if (!pode) return;
    setSalvando(true);
    try {
      await onCriar(nomeFinal, soNome ? "" : dig);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && (d.mensagem || (typeof d === "string" ? d : null))) || "Não consegui adicionar a pessoa.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => { if (!v) onFechar(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Nova pessoa na mesa</DialogTitle></DialogHeader>
        <form onSubmit={salvar} className="space-y-3" data-e2e="nova-pessoa">
          {!soNome ? (
            <>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input autoFocus value={cpf} onChange={(e) => setCpf(mascaraCpf(e.target.value))}
                  placeholder="CPF do cliente" inputMode="numeric" className="pl-9 h-11" data-e2e="nova-pessoa-cpf" />
              </div>
              {sugestoes.length > 0 && estado === "vazio" && (
                <div className="rounded-lg border divide-y overflow-hidden" data-e2e="nova-pessoa-sugestoes">
                  {sugestoes.map((c) => (
                    <button key={c.id} type="button" onClick={() => setCpf(mascaraCpf(c.cpf))}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/60">
                      <UserRound className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium truncate flex-1">{c.nome}</span>
                      <span className="text-xs text-muted-foreground font-mono">{mascaraCpf(c.cpf)}</span>
                    </button>
                  ))}
                </div>
              )}
              {estado === "buscando" && <p className="text-xs text-muted-foreground">Procurando no cadastro…</p>}
              {estado === "invalido" && <p className="text-xs text-red-500">CPF inválido — confira os números.</p>}
              {estado === "achado" && (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2" data-e2e="nova-pessoa-achado">
                  <UserCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{achado?.nome}</p>
                    <p className="text-[11px] text-muted-foreground">Cliente cadastrado{achado?.telefone ? ` · ${achado.telefone}` : ""}</p>
                  </div>
                </div>
              )}
              {estado === "novo" && (
                <>
                  <p className="text-xs text-muted-foreground">CPF sem cadastro — digite o nome para cadastrar o cliente.</p>
                  <Input autoFocus value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do cliente" className="h-11" data-e2e="nova-pessoa-nome" />
                </>
              )}
              <button type="button" onClick={() => { setSoNome(true); setSugestoes([]); }}
                className="text-xs font-semibold text-primary" data-e2e="nova-pessoa-so-nome">
                Sem CPF? Colocar só o nome
              </button>
            </>
          ) : (
            <>
              <Input autoFocus value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome" className="h-11" data-e2e="nova-pessoa-nome" />
              <p className="text-[11px] text-muted-foreground leading-snug">
                Só o nome identifica a pessoa nesta mesa, mas não vira cadastro de cliente (não acumula cashback nem histórico).
              </p>
              <button type="button" onClick={() => { setSoNome(false); setNome(""); }} className="text-xs font-semibold text-primary">
                Buscar pelo CPF
              </button>
            </>
          )}
          <Button type="submit" className="w-full h-11" disabled={!pode} data-e2e="nova-pessoa-adicionar">
            {salvando ? "Salvando..." : estado === "achado" && !soNome ? `Adicionar ${achado?.nome?.split(" ")[0] || ""}` : "Adicionar"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
